import React, { useEffect, useState } from 'react'
import { clsx } from 'clsx'
import PropTypes from 'prop-types'
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core'

import { Controller, useForm } from 'react-hook-form'
import { Label, LoadingFeedback, Select } from 'components'
import { Table, TableBody, TableCell, TableHead } from 'components/Table'

import useSnackbar from 'hooks/useSnackbar'
import useIncidentHighRisk from 'hooks/useIncidentHighRisk'
import useIncidentManegement from 'hooks/useIncidentManegement'

import schema from './schema'
import * as service from 'service'
import useStyles from './styles'

const SignificantlyAffect = ({ refresh }) => {
  const classes = useStyles()
  const snackbar = useSnackbar()
  const [loading, setLoading] = useState(false)

  const {
    isShow = false,
    incident,
    wantsToAdvanceStep,
    resetAdvanceStep,
  } = useIncidentManegement()
  const { handleHighRiskNext } = useIncidentHighRisk()

  const [selectedExtraDamageItems, setSelectedExtraDamageItems] = useState([])

  const significantlyAffect = incident?.highRisk?.affectSignificantly
  const isCreate = !significantlyAffect[0]?.answer
  const extraDamageItems = incident?.highRisk?.extraDamage

  const formMethods = useForm({
    validationSchema: schema(),
  })

  const { errors, control, getValues, setValue, triggerValidation, formState } =
    formMethods

  const { dirtyFields } = formState

  const buildDirtyAnswers = (answers = []) => {
    const dirtyFieldNames = Array.from(dirtyFields || [])

    return answers.filter((_, index) =>
      dirtyFieldNames.some((fieldName) =>
        fieldName.startsWith(`answers[${index}].`),
      ),
    )
  }

  const buildDirtyExtraDamageAnswers = (extraDamageAnswers = []) => {
    const dirtyFieldNames = Array.from(dirtyFields || [])

    return extraDamageAnswers.filter((item) =>
      dirtyFieldNames.some((fieldName) =>
        fieldName.startsWith(
          `extraDamageAnswers[${item.incidentHighRiskQuestionId}].`,
        ),
      ),
    )
  }

  const handleHighRiskOptionsSelected = () => {
    const selectedOptionId = getValues('extraDamageOrder')

    if (!selectedOptionId) {
      return
    }

    const selectedOption = extraDamageItems.find(
      (item) => String(item.id) === String(selectedOptionId),
    )

    if (!selectedOption) {
      return
    }

    setSelectedExtraDamageItems((previousItems) => {
      if (
        previousItems.some(
          (item) => String(item.id) === String(selectedOption.id),
        )
      ) {
        return previousItems
      }

      return [...previousItems, selectedOption]
    })

    setValue('extraDamageOrder', '')
  }

  const onSubmit = async (data) => {
    setLoading(true)
    try {
      if (isShow) {
        resetAdvanceStep()
        handleHighRiskNext()
        return
      }
      const answers = significantlyAffect.map((item, index) => {
        const answerValue = data?.[`answers[${index}].answer`]
        const howValue = data?.[`answers[${index}].how`]
        const whyValue = data?.[`answers[${index}].why`]
        return {
          answer: answerValue,
          how: howValue,
          why: whyValue,
          incidentHighRiskQuestionId: item.id,
        }
      })

      const extraDamageAnswers = selectedExtraDamageItems.map((item) => {
        const howValue = data?.[`extraDamageAnswers[${item.id}].how`]
        const whyValue = data?.[`extraDamageAnswers[${item.id}].why`]
        return {
          how: howValue,
          why: whyValue,
          incidentHighRiskQuestionId: item.id,
        }
      })

      const dirtyAnswers = buildDirtyAnswers(answers)
      const dirtyExtraDamageAnswers =
        buildDirtyExtraDamageAnswers(extraDamageAnswers)
      const allAnswers = [...answers, ...extraDamageAnswers]
      const allDirtyAnswers = [...dirtyAnswers, ...dirtyExtraDamageAnswers]

      if (!isCreate) {
        if (allDirtyAnswers.length) {
          await service.dponet.incidents.updateHighRiskAffectSignificantly({
            incidentId: incident?.id,
            highRiskAnswers: { answers: allDirtyAnswers },
          })
        }
      }
      if (isCreate) {
        await service.dponet.incidents.createHighRiskAffectSignificantly({
          incidentId: incident?.id,
          highRiskAnswers: { answers: allAnswers },
        })
      }

      refresh()
      resetAdvanceStep()
      handleHighRiskNext()
    } catch (error) {
      snackbar.open({
        message: 'Ocorreu um erro ao enviar as respostas',
        variant: 'error',
      })
    } finally {
      setLoading(false)
      resetAdvanceStep()
    }
  }

  useEffect(() => {
    const preSelected = (extraDamageItems || []).filter(
      (item) =>
        item?.answer?.how != null && String(item.answer.how).trim() !== '',
    )
    if (preSelected.length > 0) {
      setSelectedExtraDamageItems(preSelected)
    }
  }, [extraDamageItems])

  useEffect(() => {
    const handleValidateAdvanceStep = async () => {
      if (wantsToAdvanceStep) {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        })
        const isValid = await triggerValidation()

        if (isValid) {
          const data = getValues()
          await onSubmit(data)
        }
      }
      resetAdvanceStep()
    }
    handleValidateAdvanceStep()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsToAdvanceStep])

  return (
    <Box>
      <LoadingFeedback open={loading} />
      <form onSubmit={(e) => e.preventDefault()}>
        <Box>
          <Box mx={3} my={2}>
            {significantlyAffect?.map((significantlyAffectItem, index) => (
              <Box key={significantlyAffectItem?.id}>
                <Box mx={3} my={2} id={significantlyAffectItem?.id}>
                  <Grid container key={index}>
                    <Grid item md={10} sm={12}>
                      <Grid container>
                        <Grid item xs={12}>
                          <Typography variant="h5">
                            {significantlyAffectItem?.content}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <FormControl
                            error={!!errors?.answers?.[index]?.answer}
                          >
                            <Controller
                              control={control}
                              name={`answers[${index}].answer`}
                              error={!!errors?.answers?.[index]?.answer}
                              helperText={
                                errors?.answers?.[index]?.answer?.message
                              }
                              defaultValue={
                                significantlyAffectItem?.answer?.answer
                              }
                              as={
                                <RadioGroup row>
                                  <Box>
                                    <FormControlLabel
                                      value="Sim"
                                      control={<Radio color="primary" />}
                                      label="Sim"
                                      disabled={isShow}
                                      className={clsx({
                                        [classes.radioError]:
                                          !!errors?.answers?.[index]?.answer,
                                      })}
                                    />
                                    <FormControlLabel
                                      value="Não"
                                      control={<Radio color="primary" />}
                                      label="Não"
                                      disabled={isShow}
                                      className={clsx({
                                        [classes.radioError]:
                                          !!errors?.answers?.[index]?.answer,
                                      })}
                                    />
                                  </Box>
                                </RadioGroup>
                              }
                              onChange={([e]) => e.target.value}
                            />
                          </FormControl>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Controller
                                label="Como?"
                                control={control}
                                name={`answers[${index}].how`}
                                defaultValue={
                                  significantlyAffectItem?.answer?.how
                                }
                                error={!!errors?.answers?.[index]?.how}
                                helperText={
                                  errors?.answers?.[index]?.how?.message
                                }
                                as={
                                  <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    maxRows={5}
                                    placeholder="Como?"
                                    disabled={isShow}
                                  />
                                }
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <Controller
                                label="Por que?"
                                control={control}
                                name={`answers[${index}].why`}
                                defaultValue={
                                  significantlyAffectItem?.answer?.why
                                }
                                error={!!errors?.answers?.[index]?.why}
                                helperText={
                                  errors?.answers?.[index]?.why?.message
                                }
                                as={
                                  <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    maxRows={5}
                                    placeholder="Por Que?"
                                    disabled={isShow}
                                  />
                                }
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            ))}
          </Box>
          <Box mx={3} my={2}>
            <Label
              title="O tratamento dos dados poderá ocasionar danos materiais ou morais
              aos titulares como:"
              item
              xs={12}
            >
              <Box
                display="flex"
                justifyContent="space-evenly"
                alignItems="center"
              >
                <Box width="100%" mr={2}>
                  <Controller
                    as={
                      <Select
                        items={extraDamageItems}
                        fieldName="content"
                        fieldValue="id"
                        disabled={isShow}
                      />
                    }
                    control={control}
                    name="extraDamageOrder"
                    mode="onChange"
                  />
                </Box>
                <Button
                  onClick={handleHighRiskOptionsSelected}
                  color="primary"
                  variant="contained"
                  disabled={isShow}
                >
                  Adicionar
                </Button>
              </Box>
            </Label>
          </Box>
          <Box marginTop={8}>
            <Table size="small" noWrap>
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  <TableCell align="center" width="14%">
                    Danos Adicionados
                  </TableCell>
                  <TableCell align="center" width="43%">
                    Como?
                  </TableCell>
                  <TableCell align="center" width="43%">
                    Por Que?
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedExtraDamageItems.length > 0 ? (
                  selectedExtraDamageItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell align="center">
                        <Box>
                          <Typography variant="subtitle2">
                            {item.content}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Controller
                          control={control}
                          name={`extraDamageAnswers[${item.id}].how`}
                          defaultValue={item?.answer?.how}
                          error={!!errors?.extraDamageAnswers?.[item.id]?.how}
                          helperText={
                            errors?.extraDamageAnswers?.[item.id]?.how?.message
                          }
                          as={
                            <TextField
                              fullWidth
                              multiline
                              minRows={3}
                              maxRows={5}
                              placeholder="Como?"
                              disabled={isShow}
                            />
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Controller
                          control={control}
                          name={`extraDamageAnswers[${item.id}].why`}
                          defaultValue={item?.answer?.why}
                          error={!!errors?.extraDamageAnswers?.[item.id]?.why}
                          helperText={
                            errors?.extraDamageAnswers?.[item.id]?.why?.message
                          }
                          as={
                            <TextField
                              fullWidth
                              multiline
                              minRows={3}
                              maxRows={5}
                              placeholder="Por Que?"
                              disabled={isShow}
                            />
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      align="center"
                      style={{ color: '#666' }}
                    >
                      Adicione um Tratamento de dados
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>
      </form>
    </Box>
  )
}

SignificantlyAffect.propTypes = {
  refresh: PropTypes.func,
}

export default SignificantlyAffect
